import {
  Target, Calendar, MessageSquare, FileCheck, Megaphone, Mail, Bell,
  CalendarDays, Clock, type LucideIcon,
} from '../icons';
import { tNotif } from './notification-i18n';

export interface NotifMeta {
  Icon: LucideIcon;
  /** CSS 액센트 색 토큰(var). 아이콘 칩·미읽음 스트립 색. */
  accent: string;
  categoryKey: NotifCategoryKey;
}

/**
 * 카테고리(=메뉴) 목록 — **좌측 레일에 세우는 순서** (2026-08-18 추가).
 *
 * 그전에는 카테고리가 `notifMeta` 안에서만 살아서 "무슨 메뉴들이 있는가" 를 밖에서 물을 수
 * 없었다. 알림 팝업의 '메뉴별 알림' 이 그것을 물어야 해서 목록과 표를 밖으로 낸다.
 */
export const NOTIF_CATEGORY_KEYS = [
  'notif.cat.task',
  'notif.cat.calendar',
  'notif.cat.reservation',
  'notif.cat.approval',
  'notif.cat.board',
  'notif.cat.chat',
  'notif.cat.mail',
  'notif.cat.attendance',
  'notif.cat.system',
] as const;

export type NotifCategoryKey = (typeof NOTIF_CATEGORY_KEYS)[number];

/** 카테고리별 아이콘·색 — `notifMeta` 가 고른 결과를 여기서 꺼낸다(값의 출처는 한 곳). */
export const NOTIF_CATEGORY_META: Readonly<Record<NotifCategoryKey, { Icon: LucideIcon; accent: string }>> = {
  'notif.cat.task': { Icon: Target, accent: 'var(--accent-green)' },
  'notif.cat.calendar': { Icon: Calendar, accent: 'var(--accent-blue)' },
  'notif.cat.reservation': { Icon: CalendarDays, accent: 'var(--accent-teal)' },
  'notif.cat.approval': { Icon: FileCheck, accent: 'var(--accent-orange)' },
  'notif.cat.board': { Icon: Megaphone, accent: 'var(--accent-pink, #ec4899)' },
  'notif.cat.chat': { Icon: MessageSquare, accent: 'var(--accent-purple)' },
  'notif.cat.mail': { Icon: Mail, accent: 'var(--accent-teal)' },
  // 근태 (2026-08-30 · 판 J) — 연차촉진 통보가 처음이다.
  'notif.cat.attendance': { Icon: Clock, accent: 'var(--accent-blue)' },
  'notif.cat.system': { Icon: Bell, accent: 'var(--text-muted)' },
};

/**
 * 알림 type/source → 카테고리 메타(아이콘·색·라벨). v2.13.
 * type 은 점 네임스페이스(예: task.comment.added, calendar.event.invited,
 * board.post.liked, approval.doc.requested, mention) — prefix/키워드로 매핑.
 * 알 수 없는 타입은 system(벨) fallback.
 */
const meta = (categoryKey: NotifCategoryKey): NotifMeta => ({ ...NOTIF_CATEGORY_META[categoryKey], categoryKey });

export function notifMeta(type: string, source = ''): NotifMeta {
  const k = `${type} ${source}`.toLowerCase();
  if (k.includes('task') || k.includes('project')) {
    return meta('notif.cat.task');
  }
  if (k.includes('calendar') || k.includes('event')) {
    return meta('notif.cat.calendar');
  }
  // 예약 (2026-08-12) — approval 분기보다 먼저 본다: 예약 알림 타입에 'approved' 가 들어가
  // approval 로 오분류되는 것을 막는다(reservation.request.approved).
  if (k.includes('reservation')) {
    return meta('notif.cat.reservation');
  }
  if (k.includes('approval')) {
    return meta('notif.cat.approval');
  }
  // ★★ **근태를 board 보다 먼저 본다.** 아래 분기가 `notice` 를 게시판으로 읽어서,
  //    「촉구(notice)」 같은 근태 타입이 조용히 게시판으로 새 나간다.
  if (k.includes('hr.leave') || k.includes('attendance')) {
    return meta('notif.cat.attendance');
  }
  if (k.includes('board') || k.includes('notice') || k.includes('post')) {
    return meta('notif.cat.board');
  }
  if (k.includes('chat') || k.includes('mention') || k.includes('comment') || k.includes('message')) {
    return meta('notif.cat.chat');
  }
  if (k.includes('mail')) {
    return meta('notif.cat.mail');
  }
  return meta('notif.cat.system');
}

export function notifCategoryLabel(type: string, source = ''): string {
  return tNotif(notifMeta(type, source).categoryKey);
}
